import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    Button,
    Alert,
    Chip,
    Stack,
    TextField,
    IconButton,
} from '@mui/material';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Business } from '../../../models/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { addLead } from '../../../services/leadService';
import { Lead } from '../../../models/Lead';

const BusinessDetailsComponent: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [business, setBusiness] = useState<Business | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [creatingLead, setCreatingLead] = useState(false);
    const [existingLeadId, setExistingLeadId] = useState<string | null>(null);
    const [editingDriveLink, setEditingDriveLink] = useState(false);
    const [driveLinkValue, setDriveLinkValue] = useState('');

    useEffect(() => {
        const fetchBusinessAndLeadDetails = async () => {
            if (!id) return;

            try {
                // Fetch business details
                const businessDoc = doc(db, 'businesses', id);
                const businessSnapshot = await getDoc(businessDoc);

                if (businessSnapshot.exists()) {
                    const businessData = businessSnapshot.data();
                    const business = {
                        id: businessSnapshot.id,
                        ...businessData
                    } as Business;
                    setBusiness(business);

                    // Check for existing lead using business.id
                    const leadsRef = collection(db, 'leads');
                    const leadQuery = query(leadsRef, where('businessId', '==', business.id));
                    const leadSnapshot = await getDocs(leadQuery);
                    console.log(`Checking lead for business ${business.name} (${business.id}):`, !leadSnapshot.empty);

                    if (!leadSnapshot.empty) {
                        setExistingLeadId(leadSnapshot.docs[0].id);
                    }
                } else {
                    setError('Business not found');
                }
            } catch (err) {
                console.error('Error fetching business details:', err);
                setError('Failed to load business details');
            } finally {
                setLoading(false);
            }
        };

        fetchBusinessAndLeadDetails();
    }, [id]);

    useEffect(() => {
        if (business) {
            setDriveLinkValue(business.googleDriveLink || '');
        }
    }, [business]);

    const handleCreateLead = async () => {
        if (!business) return;

        try {
            setCreatingLead(true);
            const leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'> = {
                businessId: business.id,
                company: business.name,
                phone: business.phone || '',
                website: business.website || '',
                city: business.city || '',
                state: business.state || '',
                industryType: business.industryType || '',
                status: 'new',
                source: 'manual',
                notes: [{
                    id: Date.now().toString(),
                    text: 'Lead was manually created from business details page.',
                    createdAt: new Date()
                }],
                analyzerModel: 'V5'
            };

            const leadId = await addLead(leadData);
            setExistingLeadId(leadId);
            navigate(`/leads/lead/${leadId}`);
        } catch (err) {
            console.error('Error creating lead:', err);
            setError('Failed to create lead');
        } finally {
            setCreatingLead(false);
        }
    };

    const handleSaveDriveLink = async () => {
        if (!business) return;
        try {
            const businessDoc = doc(db, 'businesses', business.id);
            await updateDoc(businessDoc, { googleDriveLink: driveLinkValue });
            setBusiness(prev => prev ? { ...prev, googleDriveLink: driveLinkValue } : prev);
            setEditingDriveLink(false);
        } catch (err) {
            setError('Failed to update Google Drive link');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !business) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error || 'Business not found'}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h4" component="h1">
                            {business.name}
                        </Typography>
                        {!existingLeadId && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleCreateLead}
                                disabled={creatingLead}
                            >
                                {creatingLead ? 'Creating Lead...' : 'Create Lead'}
                            </Button>
                        )}
                    </Box>
                    {existingLeadId && (
                        <Paper
                            sx={{
                                p: 2,
                                bgcolor: 'success.light',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: 'success.main',
                                }
                            }}
                            onClick={() => navigate(`/leads/lead/${existingLeadId}`)}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <AssignmentIcon sx={{ color: 'common.white' }} />
                                <Typography sx={{ color: 'common.white' }}>
                                    Lead found - Click to view lead details
                                </Typography>
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon /> Business Information
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="Location"
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOnIcon fontSize="small" />
                                            {business.address}
                                        </Box>
                                    }
                                />
                            </ListItem>
                            {business.phone && (
                                <ListItem>
                                    <ListItemText
                                        primary="Phone"
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PhoneIcon fontSize="small" />
                                                <a href={`tel:${business.phone}`}>{business.phone}</a>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            )}
                            {business.website && (
                                <ListItem>
                                    <ListItemText
                                        primary="Website"
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LanguageIcon fontSize="small" />
                                                <a href={business.website} target="_blank" rel="noopener noreferrer">
                                                    {business.website}
                                                </a>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            )}
                            {business.industryType && (
                                <ListItem>
                                    <ListItemText
                                        primary="Industry"
                                        secondary={business.industryType}
                                    />
                                </ListItem>
                            )}
                            <ListItem>
                                <ListItemText
                                    primary="Google Drive Link"
                                    secondary={
                                        editingDriveLink ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={driveLinkValue}
                                                    onChange={e => setDriveLinkValue(e.target.value)}
                                                    placeholder="Paste Google Drive folder URL"
                                                />
                                                <IconButton onClick={handleSaveDriveLink} color="primary">
                                                    <SaveIcon />
                                                </IconButton>
                                            </Box>
                                        ) : business?.googleDriveLink ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <a href={business.googleDriveLink} target="_blank" rel="noopener noreferrer">
                                                    {business.googleDriveLink}
                                                </a>
                                                <IconButton onClick={() => setEditingDriveLink(true)} size="small">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Button onClick={() => setEditingDriveLink(true)} size="small" variant="outlined">
                                                Add Google Drive Link
                                            </Button>
                                        )
                                    }
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Analysis Results</Typography>
                        {business.analysis_result ? (
                            <>
                                <Typography variant="subtitle2" color="text.secondary">Score</Typography>
                                <Typography variant="body1" gutterBottom>{business.analysis_result.score}</Typography>

                                <Typography variant="subtitle2" color="text.secondary">Is outdated</Typography>
                                <Typography variant="body1" gutterBottom>
                                    {business.is_outdated ? 'Yes' : 'No'}
                                </Typography>

                                <Typography variant="subtitle2" color="text.secondary">Needs improvement</Typography>
                                <Typography variant="body1" gutterBottom>
                                    {business.analysis_result.score < 60 ? 'Yes' : 'No'}
                                </Typography>

                                <Typography variant="subtitle2" color="text.secondary">Issues</Typography>
                                <List dense>
                                    {business.analysis_result.issues?.map((issue, index) => (
                                        <ListItem key={index}>
                                            <ListItemText primary={issue} />
                                        </ListItem>
                                    ))}
                                </List>

                                <Typography variant="subtitle2" color="text.secondary">Visual score</Typography>
                                <Typography variant="body1">{business.analysis_result.visual_score}</Typography>
                            </>
                        ) : (
                            <Typography color="text.secondary">No analysis results available</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BusinessDetailsComponent; 